import React from 'react';

export const formatFieldName = (key) => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .replace(/^./, (str) => str.toUpperCase());
};

const isIsoDateTime = (value) =>
  typeof value === 'string' && /\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}/.test(value);

const isIsoDateOnly = (value) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(Z)?$/.test(value);

const parseDateOnly = (value) => {
  if (!value) return null;
  const clean = value.replace('Z', '');
  const [year, month, day] = clean.split('-').map(Number);
  if (!year || !month || !day) return null;
  // Construct as local date (no timezone shift from UTC midnight)
  return new Date(year, month - 1, day);
};

export const renderObjectDetails = (obj, itemNumber = null) => {
  if (!obj)
    return <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>N/A</span>;

  // Mileage / PerDiem style entry
  if (obj.date && (obj.location1 || obj.location2 || obj.rt_ow)) {
    const formatStore = (store) => {
      if (!store) return null;

      // Prefer explicit name/number combo when present
      if (store.name && store.storeNum) {
        return `${store.name} (Store ${store.storeNum})`;
      }
      if (store.name) {
        return store.name;
      }
      if (store.storeName && store.storeNum) {
        return `${store.storeName} (Store ${store.storeNum})`;
      }
      if (store.storeName && store.storeNumber) {
        return `${store.storeName} (#${store.storeNumber})`;
      }

      // Fallbacks using common fields from payloads
      if (store.storeName) return store.storeName;
      if (store.storeNum && (store.storeCity || store.storeState)) {
        return `Store ${store.storeNum} - ${store.storeCity || ''}${
          store.storeCity && store.storeState ? ', ' : ''
        }${store.storeState || ''}`;
      }
      if (store.storeNum) return `Store ${store.storeNum}`;

      // Last resort: city/state if that’s all we have
      if (store.storeCity || store.storeState) {
        return `${store.storeCity || ''}${
          store.storeCity && store.storeState ? ', ' : ''
        }${store.storeState || ''}`;
      }

      return null;
    };

    const startLocation = formatStore(
      obj.location1 || obj.store || obj.startLocation
    );
    const endLocation = formatStore(obj.location2 || obj.endLocation);
    const tripType =
      obj.rt_ow === 'RT'
        ? 'Round Trip'
        : obj.rt_ow === 'OW'
        ? 'One Way'
        : obj.rt_ow || null;

    return (
      <div>
        {itemNumber && <strong>Item {itemNumber}:</strong>}
        <div>
          <strong>Date:</strong> {new Date(obj.date).toLocaleDateString()}
        </div>
        {startLocation && (
          <div>
            <strong>From:</strong> {startLocation}
          </div>
        )}
        {endLocation && (
          <div>
            <strong>To:</strong> {endLocation}
          </div>
        )}
        {tripType && (
          <div>
            <strong>Trip Type:</strong> {tripType}
          </div>
        )}
      </div>
    );
  }

  if (obj.employeeFirst || obj.employeeLast || obj.employeeId) {
    return (
      <div>
        {itemNumber && <strong>Item {itemNumber}:</strong>}
        <div>
          <strong>Name:</strong> {obj.employeeFirst || ''}{' '}
          {obj.employeeLast || ''}
        </div>
        {obj.employeeId && (
          <div>
            <strong>Employee ID:</strong> {obj.employeeId}
          </div>
        )}
        {obj.dob && (
          <div>
            <strong>Date of Birth:</strong>{' '}
            {new Date(obj.dob).toLocaleDateString()}
          </div>
        )}
        {obj.hireDate && (
          <div>
            <strong>Hire Date:</strong>{' '}
            {new Date(obj.hireDate).toLocaleDateString()}
          </div>
        )}
        {obj.terminationDate && (
          <div>
            <strong>Termination Date:</strong>{' '}
            {new Date(obj.terminationDate).toLocaleDateString()}
          </div>
        )}
        {obj.ssn && (
          <div>
            <strong>SSN:</strong> ***-**-{obj.ssn.slice(-4)}
          </div>
        )}
      </div>
    );
  }

  if (obj.userName || obj.userFirst || obj.userLast) {
    return (
      <div>
        <div>
          <strong>Name:</strong> {obj.userFirst || ''} {obj.userLast || ''}
        </div>
        {obj.userName && (
          <div>
            <strong>Username:</strong> {obj.userName}
          </div>
        )}
        {obj.email && (
          <div>
            <strong>Email:</strong> {obj.email}
          </div>
        )}
      </div>
    );
  }

  if (obj.storeName || obj.storeNumber) {
    return (
      <div>
        <div>
          <strong>Store:</strong> {obj.storeName || ''} (#
          {obj.storeNumber || 'N/A'})
        </div>
        {obj.address && (
          <div>
            <strong>Address:</strong> {obj.address}
          </div>
        )}
        {obj.city && obj.state && (
          <div>
            <strong>Location:</strong> {obj.city}, {obj.state} {obj.zip || ''}
          </div>
        )}
      </div>
    );
  }

  if (obj.itemDescription || obj.quantity || obj.price) {
    return (
      <div>
        {itemNumber && <strong>Item {itemNumber}:</strong>}
        {obj.itemDescription && (
          <div>
            <strong>Description:</strong> {obj.itemDescription}
          </div>
        )}
        {obj.quantity !== undefined && (
          <div>
            <strong>Quantity:</strong> {obj.quantity}
          </div>
        )}
        {obj.price !== undefined && (
          <div>
            <strong>Price:</strong> ${obj.price.toFixed(2)}
          </div>
        )}
        {obj.total !== undefined && (
          <div>
            <strong>Total:</strong> ${obj.total.toFixed(2)}
          </div>
        )}
      </div>
    );
  }

  const entries = Object.entries(obj).filter(
    ([key, val]) =>
      val !== null && val !== undefined && val !== '' && !key.startsWith('_')
  );

  if (entries.length === 0) {
    return (
      <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>No data</span>
    );
  }

  return (
    <div>
      {itemNumber && <strong>Item {itemNumber}:</strong>}
      {entries.map(([key, val], idx) => {
        let displayValue;

        if (typeof val === 'object' && val !== null) {
          // For nested objects, delegate to renderObjectDetails to keep things readable
          displayValue = renderObjectDetails(val);
        } else if (isIsoDateTime(val)) {
          displayValue = new Date(val).toLocaleString();
        } else if (isIsoDateOnly(val)) {
          const d = parseDateOnly(val);
          displayValue = d ? d.toLocaleDateString() : val;
        } else if (typeof val === 'number') {
          if (
            key.toLowerCase().includes('price') ||
            key.toLowerCase().includes('amount') ||
            key.toLowerCase().includes('total') ||
            key.toLowerCase().includes('cost')
          ) {
            displayValue = `$${val.toFixed(2)}`;
          } else {
            displayValue = String(val);
          }
        } else {
          displayValue = String(val);
        }

        return (
          <div key={idx}>
            <strong>{formatFieldName(key)}:</strong> {displayValue}
          </div>
        );
      })}
    </div>
  );
};

export const renderValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>N/A</span>;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return (
          <span style={{ color: '#a0aec0', fontStyle: 'italic' }}>None</span>
        );
      }
      if (typeof value[0] === 'object') {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {value.map((item, idx) => (
              <div
                key={idx}
                style={{
                  padding: '8px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '4px',
                  fontSize: '13px',
                }}
              >
                {renderObjectDetails(item, idx + 1)}
              </div>
            ))}
          </div>
        );
      }
      return value.join(', ');
    }
    return renderObjectDetails(value);
  }
  if (isIsoDateTime(value)) {
    return new Date(value).toLocaleString();
  }
  if (isIsoDateOnly(value)) {
    const d = parseDateOnly(value);
    return d ? d.toLocaleDateString() : value;
  }
  return String(value);
};
